import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { VueSessionCookieService, VueUserSessionService } from '@vue/vueframework';
import { CasService } from '../services/cas.service';
import { CASUserRole } from '../types/cas-user-role.model';

@Injectable({
  providedIn: 'root'
})
export class AffiliateManagementAccessChecker {
  public constructor(
    private router: Router,
    private vueUserSessionService: VueUserSessionService,
    private vueSessionCookieService: VueSessionCookieService,
    private casService: CasService
  ) {}

  public async canActivate(): Promise<boolean> {
    const roles = [ 'SITEMANAGER_AFFILIATE_USER' ];
    const appId = 26;
    let affiliateAccessAllowed = false;

    // If the application list is present in the session use that to check for role or retrieve the roles from CAS
    if (this.vueUserSessionService.getClientsAndApplications()) {
      affiliateAccessAllowed = this.userHasOneRole(
        roles,
        appId
      );
    } else {
      const loggedInCasUserId = this.vueSessionCookieService.getAttribute('appUserId');
      const userRoles: CASUserRole[] = await this.casService.getUserRoles(Number(loggedInCasUserId)).toPromise();
  
      affiliateAccessAllowed = userRoles.some((userRole) => userRole.roleName === 'SITEMANAGER_AFFILIATE_USER');
    }

    if (!affiliateAccessAllowed) {
      this.router.navigate([ '/notAuthorized' ]);
    }
    return affiliateAccessAllowed;
  }

  /**
   *  Verifies the authenticated user has at least one of the given roles for
   *  the Site Manager application. This is needed because the
   *  VueUserSessionService.userHasOneRole() method checks against roles for the
   *  selected application (in this case, Connect UM), and we need to check against
   *  a different application.
   *
   *  Enhancing VueUserSessionService to support this
   *  behavior may be warranted if this is found to be useful for other applications
   *  but currently the assumption is that Connect UM is unique in this need.
   *
   *  @param roleNames
   *    The names to check against.
   *  @param appId
   *    The ID of the application to check roles against.
   *
   *  @return
   *    Whether the authenticated user has any of the roles in {@code roleNames}
   *    for the Site Manager application.
   */
  public userHasOneRole(roleNames: string[], appId: number): boolean {
    const apps = this.vueUserSessionService.getClientsAndApplications();
    const app = apps.find((i) => i.id === appId);
    return Boolean(app) && app.applicationRoles.some((r) => {
      return roleNames.includes(r.name);
    });
  }
}
